import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
} from '@hh/global.js'

class HHMobileMenu extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    _height: {
      state: true,
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    bodyClasses: {
      type: String,
      attribute: 'body-class',
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.breakpoint = '(min-width: 750px)'
    this.bodyClasses = ''
    this.lockScroll = false
    this.openTimeout = null
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('closeModal', this.close.bind(this))
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  updated(changedProperties) {
    clearTimeout(this.openTimeout)

    if (changedProperties.has('open')) {
      this.openTimeout = setTimeout(() => {
        if (this.open) {
          document.body.classList.add('overflow-hidden')
          document.body.classList.add('mobile-menu-open')
        } else if (!this.open) {
          document.body.classList.remove('overflow-hidden')
          document.body.classList.remove('mobile-menu-open')
        }
      }, 10)
    }
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (
      e.type === 'keyup' &&
      e.code.toUpperCase() !== 'ENTER' &&
      e.code.toUpperCase() !== 'ARROWDOWN'
    ) {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()

    const openSidePanel = document.querySelector(
      '[data-sidepanel]:not([hidden="true"])'
    )

    if (openSidePanel) openSidePanel.setAttribute('hidden', 'true')
  }

  toggleLeaveMegaMenu() {
    this.addEventListener(
      'mouseleave',
      () => {
        if (this.open) this.toggle()
      },
      false
    )
  }

  closeWithEscape(e) {
    if (e.code?.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
    // Added to support closing on Enter within Details panel
    if (
      e.code === 'Enter' &&
      e
        .composedPath()
        .some((element) => element.matches && element.matches('[data-close]'))
    ) {
      this.toggle()
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  setHeight() {
    this._height = `calc(100dvh - var(--hh-header-height) - 20px)`
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <hh-focus-trap active="${this.open}">
        <details
          ?open="${this.open}"
          @click="${this.interceptEvent}"
          ${ref(this.$container)}
        >
          <div
            class="content"
            role="dialog"
            selector
            aria-modal="true"
            aria-label="${this.label}"
            style="${styleMap({ height: this._height })}"
            ${ref(this.$details)}
            tabindex="0"
          >
            <slot name="details"></slot>
          </div>
          <summary
            @click="${this.toggle}"
            aria-expanded="${this.open}"
            aria-haspopup="dialog"
            role="button"
            ${ref(this.$container)}
            tabindex="0"
          >
            <slot
              class="${classMap({ 'is-open': this.open })}"
              name="summary"
              tabindex="-1"
            ></slot>
            <slot
              class="${classMap({ 'is-open': this.open })}"
              tabindex="-1"
            ></slot>
          </summary>
        </details>
      </hh-focus-trap>`
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-modal-background);
      backdrop-filter: var(--hh-modal-backdrop-filter);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }
  `
}

customElements.define('hh-mobile-menu', HHMobileMenu)
